/**
 * File-transfer lifecycle vocabulary — the SINGLE HOME for the persisted
 * transfer status union, the terminal/active partitions of it, and the
 * (documentation-only, not-yet-enforced) legal-transition tables.
 *
 * WHY THIS MODULE EXISTS: before it, the same five terminal statuses were
 * hand-re-enumerated in nine places — the engine's two `validateRecord`
 * helpers plus their two `isTerminal` `||`-chains (`senderResume.ts`,
 * `receiveResume.ts`), the extension's `senderState.ts`/`receiverState.ts`,
 * web's two mirrors of those, and the CLI's `transferStore.ts` (twice) —
 * with nothing tying them together. Every interruption feature had to patch
 * all of them consistently; missing one leaves a record that no prune ever
 * collects and no resume scan ever skips (a zombie). They now all import
 * from here.
 *
 * Deliberately dependency-free (no `@reflecto/core`, no storage, no IO) so
 * every surface — including the extension service worker and the CLI — can
 * import it without pulling anything else in.
 *
 * SCOPE: this module owns the PERSISTED lifecycle vocabulary (what lands in
 * `FtSenderStateRecord["status"]` / `FtReceiverStateRecord["status"]`). It
 * deliberately does NOT own the narrower 3-member UI projection
 * (`"complete" | "failed" | "cancelled"`) that `FILE_RECEIVE_DONE` and the
 * receive cards speak — that one collapses `"expired"` into `"failed"` and
 * is a surface message-protocol contract, mirrored by `@reflecto/client-ui`
 * under its decoupled-shape convention (which forbids importing package
 * types). If it is ever single-homed, it belongs here as
 * `Exclude<FtTerminalOutcome, "expired">`.
 */

/**
 * Every persisted transfer lifecycle status (architecture §2's states, relay
 * subset + PAUSED — resume spec §9), in lifecycle order: the two ACTIVE
 * statuses first, then the five TERMINAL ones.
 *
 * The string VALUES are persisted in storage/JSON on every surface and are
 * therefore wire-ish: renaming one is a migration, not a refactor.
 */
export const FT_TRANSFER_STATUSES = [
  "transferring", // active; auto-resumes after crash
  "paused", // parked; resumes only on explicit user action / resume ctrl
  "complete", // terminal
  "failed", // terminal; errorCode set
  "cancelled", // terminal
  "declined", // terminal (receiver-only tombstone)
  "expired", // terminal; server TTL / 404 path
] as const;

/** Persisted transfer lifecycle status. Derived from [FT_TRANSFER_STATUSES] so the set and the type can never drift. */
export type FtTransferStatus = (typeof FT_TRANSFER_STATUSES)[number];

/**
 * Records still owned by a loop (or waiting for one). Exactly the complement
 * of [FT_TERMINAL_STATUSES] — a status is active iff it is not terminal.
 * Active records are NEVER pruned (§8.5) and are always re-examined by a
 * resume scan (§5.1/§6.1).
 */
export const FT_ACTIVE_STATUSES: ReadonlySet<FtTransferStatus> = new Set<FtTransferStatus>([
  "transferring",
  "paused",
]);

/**
 * Absorbing statuses (§8.5 tombstones): once a record reaches one, no loop
 * touches it again — resume scans skip it, manifest validation is waived
 * (a tombstone may carry an empty `manifestJson`, §2.4.3), and the lazy
 * prune is the only thing that ever removes it.
 */
export const FT_TERMINAL_STATUSES: ReadonlySet<FtTransferStatus> = new Set<FtTransferStatus>([
  "complete",
  "failed",
  "cancelled",
  "declined",
  "expired",
]);

/**
 * The sender-side terminal set. Identical to [FT_TERMINAL_STATUSES] BY
 * CONSTRUCTION (it is the same object), exported under a role name so the
 * per-role stores read at their call sites the way they always have.
 *
 * Note the sender/receiver asymmetry is real but lives in the TRANSITIONS,
 * not in this set: a sender record is never WRITTEN `"declined"` (that is a
 * receiver-only pre-accept tombstone, see `FtSenderStateRecord.status`), yet
 * it must still be TREATED as terminal wherever one somehow appears —
 * otherwise a corrupt/foreign record would be immune to pruning forever.
 * See [SENDER_LEGAL_TRANSITIONS] for where the asymmetry is expressed.
 */
export const SENDER_TERMINAL_STATUSES = FT_TERMINAL_STATUSES;

/** The receiver-side terminal set — see [SENDER_TERMINAL_STATUSES]; same object, role-named. */
export const RECEIVER_TERMINAL_STATUSES = FT_TERMINAL_STATUSES;

/**
 * The four terminal statuses a LIVE transfer path can drive a record to.
 * Excludes `"declined"`, which is only ever an INITIAL status (the receiver's
 * pre-accept tombstone, §8.3) and never a transition target.
 *
 * This is the type both `persistTerminal` helpers and the receiver resume's
 * `onTerminal` callback speak (`ResumeTerminalOutcome` is an alias of it).
 */
export type FtTerminalOutcome = Exclude<FtTransferStatus, "transferring" | "paused" | "declined">;

/** True iff `status` is one of the seven known statuses — the §2.4 drop-on-load gate against corrupt persisted JSON. */
export function isKnownTransferStatus(status: string): status is FtTransferStatus {
  return (FT_TRANSFER_STATUSES as readonly string[]).includes(status);
}

/** True iff `status` is terminal for a SENDER record (§8.5) — see [SENDER_TERMINAL_STATUSES]. */
export function isTerminalSenderStatus(status: FtTransferStatus): boolean {
  return SENDER_TERMINAL_STATUSES.has(status);
}

/** True iff `status` is terminal for a RECEIVER record (§8.5) — see [RECEIVER_TERMINAL_STATUSES]. */
export function isTerminalReceiverStatus(status: FtTransferStatus): boolean {
  return RECEIVER_TERMINAL_STATUSES.has(status);
}

/** True iff a loop still owns (or may re-own) this record — the complement of terminal. */
export function isActiveTransferStatus(status: FtTransferStatus): boolean {
  return FT_ACTIVE_STATUSES.has(status);
}

/** Which side of a transfer a record belongs to — the two transition tables differ (see below). */
export type FtTransferRole = "sender" | "receiver";

/**
 * Sender-side legal transitions, DESCRIPTIVE of today's call sites:
 *
 *   - `transferring` is the only initial status (`sendFile.ts`'s P-S1 create).
 *   - `transferring → paused` — checkpoint-resolved `UploadPausedError`
 *     (`sendFile.ts` P-S3, `senderResume.ts`'s resumed-loop mirror).
 *   - `paused → transferring` — a resume flips it back before reconciling
 *     (`senderResume.ts`).
 *   - `paused → complete` — the §9.3 post-demotion early-complete override
 *     (`sendFile.ts`), the one terminal a paused record reaches without
 *     passing back through `transferring`.
 *   - `paused → cancelled | failed` — the no-live-loop peer-ctrl rows
 *     (`pausedTransferTerminal.ts` + the surfaces' local Cancel).
 *     NOT `expired`: expiry is only ever detected by a live loop, which by
 *     then has already flipped the record back to `transferring`.
 *   - `declined` never appears: it is a receiver-only tombstone.
 *   - Terminal statuses are absorbing (empty target lists).
 */
export const SENDER_LEGAL_TRANSITIONS: Readonly<
  Record<FtTransferStatus, readonly FtTransferStatus[]>
> = {
  transferring: ["paused", "complete", "failed", "cancelled", "expired"],
  paused: ["transferring", "complete", "failed", "cancelled"],
  complete: [],
  failed: [],
  cancelled: [],
  declined: [],
  expired: [],
};

/**
 * Receiver-side legal transitions, DESCRIPTIVE of today's call sites:
 *
 *   - Initial statuses are `transferring` (written at accept) or a pre-accept
 *     `declined` / `cancelled` tombstone (§8.3) — tombstones are BORN
 *     terminal, they are not transitioned into.
 *   - `transferring → complete | failed | cancelled | expired` —
 *     `receiveResume.ts`/`receiveHost.ts`'s P-R4 terminals.
 *   - `paused → transferring` — the receiver-pause primitive was CUT, so a
 *     receiver never writes `paused`; the resume entry's defensive flip is
 *     the only edge out of it, kept here because the code still handles it.
 *   - Terminal statuses are absorbing (empty target lists).
 */
export const RECEIVER_LEGAL_TRANSITIONS: Readonly<
  Record<FtTransferStatus, readonly FtTransferStatus[]>
> = {
  transferring: ["complete", "failed", "cancelled", "expired"],
  paused: ["transferring"],
  complete: [],
  failed: [],
  cancelled: [],
  declined: [],
  expired: [],
};

/** The transition table for `role`. */
export function legalTransitionsFor(
  role: FtTransferRole
): Readonly<Record<FtTransferStatus, readonly FtTransferStatus[]>> {
  return role === "sender" ? SENDER_LEGAL_TRANSITIONS : RECEIVER_LEGAL_TRANSITIONS;
}

/**
 * True iff `from → to` is a legal transition for `role`. A no-op transition
 * (`from === to`) is always legal — every persist path rewrites the whole
 * record, so re-saving an unchanged status is routine.
 */
export function isLegalTransition(
  role: FtTransferRole,
  from: FtTransferStatus,
  to: FtTransferStatus
): boolean {
  if (from === to) return true;
  return legalTransitionsFor(role)[from].includes(to);
}

/**
 * Throw unless `from → to` is legal for `role`.
 *
 * NOT WIRED YET, ON PURPOSE: making the engine enforce this would change
 * behavior (an illegal transition that silently succeeds today would start
 * throwing), which is out of scope for the refactor that introduced this
 * module. It exists so the tables are exercised and reviewable now, and so
 * enforcement is a one-line change per persist site later.
 *
 * The message carries only status names — never a transferId, filename, or
 * manifest field (security.md).
 */
export function assertTransition(
  role: FtTransferRole,
  from: FtTransferStatus,
  to: FtTransferStatus
): void {
  if (!isLegalTransition(role, from, to)) {
    throw new Error(`illegal ${role} transfer transition: ${from} -> ${to}`);
  }
}
